import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../core/store/store';
import { getApiBaseUrl } from '../../../core/config/api';
import { useRealtimeInvalidation } from '../../../shared/realtime/RealtimeProvider';

interface RecruitmentRole {
  id: number;
  role_id: number;
  role_name: string;
  role_description: string | null;
  mission: string | null;
  ordre: number;
}
interface AvailableRole {
  id: number;
  role_name: string;
  description: string | null;
}
interface ManageData {
  enabled: boolean;
  message: string | null;
  roles: RecruitmentRole[];
  availableRoles: AvailableRole[];
}

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } as const;
};

/**
 * Parse défensif d'une réponse API. Si le serveur renvoie du HTML (route absente
 * du binaire déployé → SPA index.html), `res.json()` échouerait sur un message
 * illisible (« JSON.parse: unexpected character… ») : on lève ici une erreur
 * explicite et actionnable à la place.
 */
async function readJson(res: Response): Promise<any> {
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      res.status === 404
        ? 'Route /api/recruitment introuvable : le serveur doit être redéployé (npm run build:server + redémarrage).'
        : `Réponse serveur invalide (HTTP ${res.status}). Le serveur doit peut-être être redéployé.`,
    );
  }
  return res.json();
}

/**
 * Gestion du recrutement d'équipe (Founder + directions). Active/désactive l'annonce,
 * message d'intro, et ajoute/retire les rôles recherchés (avec leur mission).
 */
const RecruitmentManagement: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const rid = currentUser?.roles_id ?? 99;
  const hasAccess = rid >= 1 && rid <= 4;

  const [data, setData] = useState<ManageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingMsg, setSavingMsg] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [messageDraft, setMessageDraft] = useState('');
  const [newRoleId, setNewRoleId] = useState<number | ''>('');
  const [newMission, setNewMission] = useState('');
  const [busyRoleId, setBusyRoleId] = useState<number | null>(null);

  const base = `${getApiBaseUrl(true)}/recruitment`;

  const fetchManage = useCallback(async () => {
    try {
      const res = await fetch(`${base}/manage`, { headers: authHeaders(), credentials: 'include' });
      const json = await readJson(res);
      if (!res.ok || !json.success) throw new Error(json.error || 'Erreur de chargement');
      setData(json.data);
      setMessageDraft(json.data.message ?? '');
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    if (hasAccess) void fetchManage();
    else setLoading(false);
  }, [hasAccess, fetchManage]);

  // Synchro multi-admin en direct.
  useRealtimeInvalidation('recruitment', () => {
    if (hasAccess) void fetchManage();
  });

  const flash = (msg: string) => {
    setNotice(msg);
    setError(null);
    window.setTimeout(() => setNotice(null), 2500);
  };

  const putState = async (body: Record<string, unknown>, okMsg: string) => {
    try {
      const res = await fetch(base, { method: 'PUT', headers: authHeaders(), credentials: 'include', body: JSON.stringify(body) });
      const json = await readJson(res);
      if (!res.ok || !json.success) throw new Error(json.error || 'Erreur');
      setData((prev) => (prev ? { ...prev, ...json.data } : json.data));
      flash(okMsg);
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    }
  };

  const toggleEnabled = () => {
    if (!data) return;
    void putState({ enabled: !data.enabled }, !data.enabled ? 'Recrutement activé' : 'Recrutement désactivé');
  };

  const saveMessage = async () => {
    setSavingMsg(true);
    await putState({ message: messageDraft }, 'Message enregistré');
    setSavingMsg(false);
  };

  const addRole = async () => {
    if (!newRoleId) return;
    setBusyRoleId(-1);
    try {
      const res = await fetch(`${base}/roles`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ role_id: newRoleId, mission: newMission }),
      });
      const json = await readJson(res);
      if (!res.ok || !json.success) throw new Error(json.error || 'Erreur');
      setData((prev) => (prev ? { ...prev, ...json.data } : prev));
      setNewRoleId('');
      setNewMission('');
      flash('Rôle ajouté');
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    } finally {
      setBusyRoleId(null);
    }
  };

  const saveMission = async (id: number, mission: string) => {
    setBusyRoleId(id);
    try {
      const res = await fetch(`${base}/roles/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ mission }),
      });
      const json = await readJson(res);
      if (!res.ok || !json.success) throw new Error(json.error || 'Erreur');
      setData((prev) => (prev ? { ...prev, ...json.data } : prev));
      flash('Mission enregistrée');
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    } finally {
      setBusyRoleId(null);
    }
  };

  const removeRole = async (id: number) => {
    setBusyRoleId(id);
    try {
      const res = await fetch(`${base}/roles/${id}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
      const json = await readJson(res);
      if (!res.ok || !json.success) throw new Error(json.error || 'Erreur');
      setData((prev) => (prev ? { ...prev, ...json.data } : prev));
      flash('Rôle retiré');
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    } finally {
      setBusyRoleId(null);
    }
  };

  const selectableRoles = useMemo(() => {
    if (!data) return [];
    const taken = new Set(data.roles.map((r) => r.role_id));
    return data.availableRoles.filter((r) => !taken.has(r.id));
  }, [data]);

  if (!hasAccess) {
    return (
      <div className={embedded ? '' : 'p-6'}>
        <div className="rounded-xl border border-red-500/40 bg-red-950/30 p-6 text-center text-cockpit-text">
          <h2 className="text-lg font-bold text-red-200">Accès refusé</h2>
          <p className="mt-2 text-sm text-red-100">Réservé au Founder et aux directions.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="p-6 text-cockpit-text-2">Chargement…</div>;
  }

  return (
    <div className={embedded ? '' : 'mx-auto max-w-4xl px-4 py-6'}>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-cockpit-text">Recrutement d'équipe</h1>
          <p className="text-sm text-cockpit-text-2">Annonce affichée sur la page d'accueil publique.</p>
        </div>
        {data && (
          <button
            type="button"
            onClick={toggleEnabled}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              data.enabled
                ? 'bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 border border-emerald-500/50'
                : 'bg-white/5 text-cockpit-text-2 hover:bg-white/10 border border-cockpit-line/60'
            }`}
          >
            <span className={`h-2.5 w-2.5 rounded-full ${data.enabled ? 'bg-emerald-400' : 'bg-slate-500'}`} />
            {data.enabled ? 'Recrutement ACTIF' : 'Recrutement inactif'}
          </button>
        )}
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</div>}
      {notice && <div className="mb-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">{notice}</div>}

      {/* Message d'intro */}
      <section className="mb-6 rounded-xl border border-cockpit-line/90 bg-cockpit-panel/90 p-4 backdrop-blur-sm">
        <label className="mb-2 block text-sm font-semibold text-cockpit-text">Message d'introduction (optionnel)</label>
        <textarea
          value={messageDraft}
          onChange={(e) => setMessageDraft(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Ex. Nous cherchons du renfort pour encadrer la communauté…"
          className="w-full rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:border-emerald-500/50 focus:outline-none"
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-cockpit-muted">{messageDraft.length}/500</span>
          <button
            type="button"
            onClick={saveMessage}
            disabled={savingMsg}
            className="rounded-lg bg-[#6366f1]/80 px-4 py-1.5 text-sm font-semibold text-cockpit-text transition-colors hover:bg-[#6366f1] disabled:opacity-50"
          >
            Enregistrer le message
          </button>
        </div>
      </section>

      {/* Ajout d'un rôle */}
      <section className="mb-6 rounded-xl border border-cockpit-line/90 bg-cockpit-panel/90 p-4 backdrop-blur-sm">
        <p className="mb-3 text-sm font-semibold text-cockpit-text">Ajouter un poste recherché</p>
        <div className="flex flex-col gap-3">
          <select
            value={newRoleId}
            onChange={(e) => setNewRoleId(e.target.value ? Number(e.target.value) : '')}
            className="w-full rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-2 text-sm text-cockpit-text focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="">— Choisir un rôle —</option>
            {selectableRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.role_name}
                {r.description ? ` — ${r.description}` : ''}
              </option>
            ))}
          </select>
          <textarea
            value={newMission}
            onChange={(e) => setNewMission(e.target.value)}
            rows={2}
            placeholder="Mission / description du poste (affichée dans le volet)"
            className="w-full rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:border-emerald-500/50 focus:outline-none"
          />
          <button
            type="button"
            onClick={addRole}
            disabled={!newRoleId || busyRoleId === -1}
            className="self-start rounded-lg bg-emerald-500/20 px-4 py-1.5 text-sm font-semibold text-emerald-200 transition-colors hover:bg-emerald-500/30 border border-emerald-500/50 disabled:opacity-50"
          >
            + Ajouter le rôle
          </button>
        </div>
        {selectableRoles.length === 0 && (
          <p className="mt-2 text-xs text-cockpit-muted">Tous les rôles disponibles ont déjà été ajoutés.</p>
        )}
      </section>

      {/* Liste des rôles ajoutés */}
      <section>
        <p className="mb-3 text-sm font-semibold text-cockpit-text">
          Postes recherchés {data ? `(${data.roles.length})` : ''}
        </p>
        {data && data.roles.length === 0 && (
          <p className="rounded-lg border border-cockpit-line/90 bg-cockpit-panel/70 px-4 py-6 text-center text-sm text-cockpit-muted">
            Aucun poste pour l'instant. Ajoute un rôle ci-dessus.
          </p>
        )}
        <div className="space-y-3">
          {data?.roles.map((role) => (
            <RoleRow
              key={role.id}
              role={role}
              busy={busyRoleId === role.id}
              onSaveMission={(m) => saveMission(role.id, m)}
              onRemove={() => removeRole(role.id)}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

const RoleRow: React.FC<{
  role: RecruitmentRole;
  busy: boolean;
  onSaveMission: (mission: string) => void;
  onRemove: () => void;
}> = ({ role, busy, onSaveMission, onRemove }) => {
  const [mission, setMission] = useState(role.mission ?? '');
  const dirty = mission !== (role.mission ?? '');

  useEffect(() => {
    setMission(role.mission ?? '');
  }, [role.mission]);

  return (
    <div className="rounded-lg border border-cockpit-line/90 bg-cockpit-panel/90 p-4 backdrop-blur-sm">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="font-semibold text-cockpit-text">{role.role_name}</span>
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-200 transition-colors hover:bg-red-500/20 disabled:opacity-50"
        >
          Retirer
        </button>
      </div>
      <textarea
        value={mission}
        onChange={(e) => setMission(e.target.value)}
        rows={2}
        placeholder="Mission / description du poste"
        className="w-full rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:border-emerald-500/50 focus:outline-none"
      />
      {dirty && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => onSaveMission(mission)}
            disabled={busy}
            className="rounded-lg bg-[#6366f1]/80 px-3 py-1 text-xs font-semibold text-cockpit-text transition-colors hover:bg-[#6366f1] disabled:opacity-50"
          >
            Enregistrer la mission
          </button>
        </div>
      )}
    </div>
  );
};

export default RecruitmentManagement;
